<template>
  <div class="fab-chat">
    <div v-show="showPropChat" class="panel panel-default">
      <div class="d-flex bd-highlight bot-header">
        <div class="d-flex align-self-center">
          <div class="tpl-avatar-image">
            <div class="lazy-img">
              <div class="bot-avatar-icon">{{ assistantInitials }}</div>
            </div>
          </div>
          <div class="d-flex flex-column justify-content-center">
            <span class="bot-name p-2 flex-fill">{{ assistantName }}</span>
            <div class="d-flex align-items-center">
              <span class="online-status-indicator" />
              <span class="online-text">En Línea</span>
            </div>
          </div>
        </div>
        <div class="ml-auto align-self-center bot-header-btn">
          <button
            class="btn refresh-button bot-header-btn"
            @click="restartChat()"
          >
            <i class="fas fa-redo" />
          </button>
          <button class="btn close-button bot-header-btn" @click="hideChat()">
            <i class="fa fa-times" />
          </button>
        </div>
        <div class="clearfix" />
      </div>

      <!-- Mensaje de error -->
      <error v-if="errorMessage" :message="errorMessage" />

      <div class="panel-body">
        <div id="webchat" />
      </div>
    </div>
  </div>
</template>
<script>
import { API, NODE_ENV, AUTH, DETICKET_API } from '@/config';
import Error from 'custom-error';
import objectToFormData from 'object-to-formdata';
import { uploadTicketFile as _uploadTicketFile } from '@/services/common/virtualassistant.api';

export default {
  components: {
    Error,
  },
  props: {
    profile: {
      type: Object,
      require: true,
      default: () => {},
    },
    showPropChat: {
      type: Boolean,
      require: true,
      default: false,
    },
    assistantName: {
      type: String,
      default: 'Asistente Virtual',
    },
  },
  methods: {
    hideChat() {
      this.$emit('hideChat');
    },
    replaceTrailingSlash(url) {
      if (url.lastIndexOf('/') === url.length - 1) {
        url = url.slice(0, -1);
      }

      return url;
    },
    restartChat() {
      this.startConversation();
    },
    async startConversation() {
      this.errorMessage = '';

      const ready = await this.ensureWebChatReady();
      if (!ready) {
        return;
      }

      const styleSet = window.WebChat.createStyleSet(this.styleSet);

      // Configuración de los estilos
      styleSet.textContent = {
        ...styleSet.textContent,
        ...this.textContent,
      };

      var theURL = process.env.VUE_APP_CHAT_BOT_URL;
      var environmentEndPoint = theURL.slice(
        0,
        theURL.indexOf('/powervirtualagents')
      );
      const apiVersion = theURL
        .slice(theURL.indexOf('api-version'))
        .split('=')[1];
      const regionalChannelSettingsURL = `${environmentEndPoint}/powervirtualagents/regionalchannelsettings?api-version=${apiVersion}`;

      let directline;
      try {
        const response = await fetch(regionalChannelSettingsURL);
        const data = await response.json();
        directline = data.channelUrlsById.directline;
      } catch (error) {
        return;
      }

      this.webChatStore = window.WebChat.createStore(
        {},
        ({ dispatch }) => (next) => async (action) => {
          //Iniciar conversación
          if (action.type === 'DIRECT_LINE/CONNECT_FULFILLED') {
            dispatch({
              meta: {
                method: 'keyboard',
              },
              payload: {
                activity: {
                  channelData: {
                    postBack: true,
                  },
                  value: {
                    userId: this.profile.userId,
                    fullName: this.profile.firstname,
                    baseUrl: this.replaceTrailingSlash(
                      NODE_ENV === 'development'
                        ? API.BASE_URL_TEST
                        : API.BASE_URL
                    ),
                    clientUrl: this.replaceTrailingSlash(
                      NODE_ENV === 'development'
                        ? AUTH.POST_LOGOUT_REDIRECT_URI_TEST
                        : AUTH.POST_LOGOUT_REDIRECT_URI
                    ),
                    deTicketUrl: this.replaceTrailingSlash(
                      DETICKET_API.BASE_URL
                    ),
                  },
                  name: 'startConversation',
                  type: 'event',
                },
              },
              type: 'DIRECT_LINE/POST_ACTIVITY',
            });
          }

          // Interceptar el mensaje antes de enviarlo para validar cantidad de caracteres
          if (action.type === 'WEB_CHAT/SEND_MESSAGE') {
            const message = action.payload.text;
            const maxLength = 2500; // Límite de caracteres

            const fullMessage = `${this.originalComment} ${message}`; //tomamos en cuenta mensaje original cuando se está añadiendo información adicional
            // Verifica si el mensaje excede el límite de caracteres
            if (message && fullMessage.length > maxLength) {
              this.errorMessage = `Tu mensaje excede el límite de ${maxLength} caracteres. Por favor, ajusta tu mensaje.`;
              return; // Evitar el envío del mensaje
            } else {
              this.errorMessage = ''; //limpiamos para poder quitar la alerta luego de un mensjae que cumple con las validaciones
            }
          }

          //Interceptar mensaje con archivo adjunto
          if (
            action.type === 'WEB_CHAT/SET_SEND_BOX_ATTACHMENTS' &&
            action.payload.attachments &&
            action.payload.attachments.length > 0 &&
            this.lastBotMessage &&
            this.lastBotMessage.includes(
              'Por favor adjunta y envía el archivo que deseas añadir como evidencia del caso.'
            )
          ) {
            // Aquí interceptamos la acción antes de que se envíe
            const attachments = action.payload.attachments;

            if (attachments.length > 1) {
              this.errorMessage =
                'Solo se permite subir un archivo a la vez. Por favor, intenta nuevamente.';
              return; // Si hay más de un archivo, se muestra el mensaje de error y se detiene
            }

            // Limpiar errorMessage si hay un archivo y se cumple la primera validación
            this.errorMessage = '';

            // Validar el archivo
            const file = attachments[0].blob;
            const maxSize = 10 * 1024 * 1024; // 10 MB en bytes

            if (file.size > maxSize) {
              // Mostrar un mensaje de advertencia si el archivo es demasiado grande
              this.errorMessage =
                'El archivo debe ser menor a 10 MB. Por favor, intenta nuevamente.';
              return; // Detener el flujo si hay un error
            }

            // Limpiar errorMessage si todas las validaciones son exitosas
            this.errorMessage = '';

            const params = objectToFormData({
              file: file,
            });

            // Enviar el mensaje al bot para ser mostrado en el chat
            dispatch({
              type: 'DIRECT_LINE/INCOMING_ACTIVITY',
              payload: {
                activity: {
                  id: Date.now().toString(), // Un ID único para evitar conflictos
                  text: '',
                  from: { id: 'me', role: 'user' }, // Muestra el mensaje como si fuera del usuario
                  type: 'message',
                  timestamp: new Date().toISOString(),
                  attachments: [
                    {
                      contentType: file.type,
                      contentUrl: '',
                      name: file.name,
                      blob: file,
                    },
                  ],
                },
              },
            });

            //subir el archivo a Azure FIleShare
            await _uploadTicketFile(params).then((result) => {
              const fileObject = {
                originalFilename: file.name,
                azureFilename: result.data,
              };

              // Enviar el mensaje al bot con el nombre del archivo subido a Azure FileShare como un evento personalizado
              dispatch({
                type: 'DIRECT_LINE/POST_ACTIVITY',
                meta: { method: 'keyboard' },
                payload: {
                  activity: {
                    type: 'event',
                    name: 'fileUpload', // Nombre del evento personalizado
                    value: fileObject,
                  },
                },
              });

              // Limpiar los adjuntos del cuadro de envío después de que el archivo se haya procesado
              setTimeout(() => {
                dispatch({
                  type: 'WEB_CHAT/SET_SEND_BOX_ATTACHMENTS',
                  payload: { attachments: [] }, // Esto limpia los adjuntos
                });
              }, 100);

              // Limpiar errorMessage si todas las validaciones son exitosas
              this.errorMessage = '';
            });
          }

          // Interceptar mensajes recibidos por el bot
          if (
            action.type === 'DIRECT_LINE/INCOMING_ACTIVITY' &&
            action.payload.activity.from.role === 'bot'
          ) {
            this.lastBotMessage = action.payload.activity.text;

            if (
              this.lastBotMessage &&
              this.lastBotMessage.includes('Habias escrito:')
            ) {
              const regex = /"(.*?)"/; // Busca el texto entre comillas dobles
              const match = this.lastBotMessage.match(regex);

              if (match && match[1]) {
                this.originalComment = match[1];
              }
            }
          }

          return next(action);
        }
      );

      const response = await fetch(theURL);
      if (!response.ok) throw new Error('Error al obtener el token');

      const { token: conversationToken } = await response.json();
      this.conversationToken = conversationToken;

      // Configuración de DirectLine y opciones de estilo
      const directLine = window.WebChat.createDirectLine({
        domain: `${directline}v3/directline`,
        token: conversationToken,
      });

      const styleOptions = {
        botAvatarInitials: this.assistantInitials,
        userAvatarInitials: this.userInitials,
        hideUploadButton: false,
        sendBoxTextWrap: true,
        suggestedActionLayout: 'stacked',
      };

      // Renderizar WebChat con las opciones definidas
      window.WebChat.renderWebChat(
        {
          directLine,
          store: this.webChatStore,
          styleSet,
          styleOptions,
          locale: 'es-ES', // Configuración del idioma
        },
        document.getElementById('webchat')
      );

      // Asegúrate de ejecutar este código después de que WebChat esté completamente renderizado.
      // Seleccionamos el sendBox con la clase principal que no cambia, ignorando el nombre generado dinámicamente
      const sendBox = document.querySelector('.webchat__send-box');

      if (sendBox) {
        sendBox.style.borderTop = 'none'; // Eliminar borde superior
        sendBox.style.minHeight = '62px'; // Ajustar altura mínima
        sendBox.style.padding = '14px 16px';

        // Seleccionar el contenedor interno del sendBox
        const sendBoxMain = sendBox.querySelector('.webchat__send-box__main');
        if (sendBoxMain) {
          sendBoxMain.style.alignItems = 'stretch'; // Ajuste de alineación
          sendBoxMain.style.minHeight = '62px'; // Ajuste de altura mínima
          sendBoxMain.style.borderRadius = '10px';

          const sendButton = sendBoxMain.querySelector('.webchat__send-button');
          if (sendButton) {
            sendButton.style.backgroundColor = '#2DB782'; // Fondo verde
            sendButton.style.borderRadius = '50%'; // Forma circular
            sendButton.style.color = '#FFFFFF'; // Color del ícono
            sendButton.style.width = '38px';
            sendButton.style.height = '38px';
            sendButton.style.padding = '8px';
            sendButton.style.margin = '10px';

            const sendIcon = sendBoxMain.querySelector('.webchat__send-icon');
            if (sendIcon) {
              const newSVG = `<svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path id="Vector" d="M17.6163 8.33058L1.1136 0.0792219C0.984254 0.0145363 0.838969 -0.0113818 0.695237 0.00458842C0.551505 0.0205587 0.415454 0.0777363 0.303464 0.169237C0.196515 0.258871 0.116688 0.376561 0.0729583 0.509075C0.0292284 0.64159 0.0233291 0.783676 0.0559235 0.91936L2.04375 8.24806H10.5276V9.74831H2.04375L0.0259186 17.0545C-0.0046666 17.1678 -0.00823684 17.2867 0.0154951 17.4017C0.0392269 17.5166 0.0895989 17.6244 0.16256 17.7163C0.235522 17.8082 0.329037 17.8818 0.435586 17.931C0.542136 17.9802 0.658746 18.0037 0.776042 17.9997C0.893468 17.999 1.00909 17.9707 1.1136 17.9172L17.6163 9.6658C17.7392 9.60285 17.8423 9.50721 17.9143 9.38942C17.9863 9.27162 18.0244 9.13625 18.0244 8.99819C18.0244 8.86013 17.9863 8.72475 17.9143 8.60696C17.8423 8.48916 17.7392 8.39353 17.6163 8.33058Z" fill="white"/>
                            </svg>`;
              sendButton.innerHTML = newSVG;
            }
          }
        }
      }
    },
    // Función para convertir ArrayBuffer a Base64
    arrayBufferToBase64(buffer) {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    },
    async ensureWebChatReady(maxRetries = 30, delay = 200) {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (window.WebChat && window.WebChat.createStyleSet) {
          return true;
        }
        await new Promise((r) => setTimeout(r, delay));
      }
      return false;
    },
  },
  computed: {
    assistantInitials() {
      let nameParts = this.assistantName.split(' ');

      return nameParts.map((part) => part[0].toUpperCase()).join('');
    },
    userInitials() {
      return this.profile.firstname.charAt(0);
    },
  },
  data: () => ({
    styleSet: {
      backgroundColor: '#d9e5df',
      bubbleBackground: '#ffffff',
      bubbleBorderRadius: '0px 10px 10px 10px',
      bubbleTextColor: '#000000',
      botAvatarBackgroundColor: '#FF7058',
      userAvatarBackgroundColor: '#2DB782',
      bubbleFromUserBackground: '#2DB782',
      bubbleFromUserBorderRadius: '10px 0px 10px 10px',
      bubbleFromUserTextColor: '#ffffff',
      rootHeight: '600px',
      rootWidth: '100%',
      suggestedActionBorderColor: 'rgba(48 170 125)', // defaults to accent
      suggestedActionBorderStyle: 'solid',
      suggestedActionBackground: 'red',
      suggestedActionBorder: undefined, // split into 3, null
      suggestedActionTextColor: 'rgba(48 170 125)',
      suggestedActionBoarderWidth: 1,
      suggestedActionBorderRadius: 30,
      suggestedActionImageHeight: 20,
      suggestedActionHeight: 35,
    },
    textContent: {
      fontFamily: 'Lato',
      fontWeight: '500',
      scroll: '',
    },
    errorMessage: '',
    originalComment: '',
    lastBotMessage: '',
    webChatStore: null,
    startedOnce: false,
  }),
  watch: {
    showPropChat(newValue) {
      if (newValue === true && !this.webChatStore && !this.startedOnce) {
        this.startedOnce = true;
        this.startConversation();
      }
    },
  },
};
</script>

<style scoped lang="scss">
$primary-color: #2db782;
$panel-bg-color: #727272;
$alternate-color: #ff7058;

.fab-chat {
  position: fixed;
  bottom: 1.4rem;
  right: 8rem;
  float: right;
  z-index: 1000;
  max-width: 370px;
  min-width: 370px;

  .bot-header {
    height: 74px;
    padding: 16px;
  }

  .bot-header-btn {
    color: #727272;
  }

  .online-status-indicator {
    width: 8px;
    height: 8px;
    background-color: #28a745; // Color verde para el estado en línea
    border-radius: 50%;
    margin-right: 5px;
  }

  .online-text {
    font-size: 12px;
    color: #6c757d; // Color gris para el texto "En Línea"
    margin: 0; // Quita márgenes adicionales
  }

  .d-flex.flex-column {
    justify-content: center; // Centra verticalmente ambos textos (nombre y estado)
  }

  .d-flex.align-items-center {
    margin-top: -10px; // Ajusta el espacio entre el nombre y el estado "En Línea"
    margin-left: 10px;
  }

  .panel {
    background-color: #fff;
    -webkit-box-shadow: rgb(0 0 0 / 25%) 0 4px 16px;
    box-shadow: 0 4px 16px rgb(0 0 0 / 25%);
    border-radius: 15px;
  }

  .btn-circle.btn-xl {
    width: 60px;
    height: 60px;
    padding: 10px 16px;
    border-radius: 35px;
    font-size: 24px;
    line-height: 1.33;
  }

  .btn-circle {
    width: 30px;
    height: 30px;
    padding: 6px 0px;
    border-radius: 15px;
    text-align: center;
    font-size: 12px;
    line-height: 1.42857;
  }

  .bot-name {
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 0; // Quita margen inferior
  }

  .panel-heading {
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    padding: 0 24px;
  }

  .panel-body {
    background-color: $panel-bg-color;
  }

  .bot-avatar-image {
    width: 40px;
    height: 40px;
    border-radius: 30px;
  }

  .bot-avatar-icon {
    width: 40px;
    height: 40px;
    border-radius: 30px;
    background: $alternate-color;
    text-align: center;
    color: #fff;
    padding: 9px;
  }

  .tpl-avatar-image {
    padding-left: 5px;
  }

  .close-button i {
    font-size: 25px;
    font-weight: 100;
  }
}
</style>
